import { useLayoutEffect } from 'react';
import { animateScroll } from 'react-scroll';

type UseScrollToTopProps = {
  shouldScrollToTop: boolean;
};

/**
 * Hook to scroll to the top of the page with manual scroll restoration.
 * Disables browser's automatic scroll restoration to prevent conflicts,
 * performs an immediate scroll to top, and restores default behavior on cleanup.
 */
const useScrollToTop = ({ shouldScrollToTop }: UseScrollToTopProps) => {
  useLayoutEffect(() => {
    let restore: () => void;
    if (shouldScrollToTop) {
      // Disable browser's automatic scroll restoration to prevent conflicts with browser's default scroll restoration behavior.
      if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      // Use duration: 0 for immediate scroll to avoid race conditions with browser
      animateScroll.scrollToTop({ smooth: false, duration: 0 });

      restore = () => {
        if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
          history.scrollRestoration = 'auto';
        }
      };
      window.addEventListener('beforeunload', restore);
    }

    return () => {
      window.removeEventListener('beforeunload', restore);
      restore?.();
    };
  }, [shouldScrollToTop]);
};

export default useScrollToTop;
